import moment from 'moment';
import { useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAxios } from '../../utils/hook/useAxios';

const FUEL_OPTIONS = ['Vide', '1/4', '1/2', '3/4', 'Plein'];

function ReservationFlow({ reservation, onReservationUpdated }) {
  const [checkinPhotos, setCheckinPhotos] = useState([]);
  const [checkoutPhotos, setCheckoutPhotos] = useState([]);
  const [kmStart, setKmStart] = useState('');
  const [fuelStart, setFuelStart] = useState('');
  const [kmEnd, setKmEnd] = useState('');
  const [fuelEnd, setFuelEnd] = useState('');
  const [commentCheckout, setCommentCheckout] = useState('');
  const [loading, setLoading] = useState(false);

  const axios = useAxios();

  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', 'r6s4af83');
    try {
      const response = await fetch('https://api.cloudinary.com/v1_1/dtrkcpsky/image/upload', {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) throw new Error('Erreur upload Cloudinary');
      const data = await response.json();
      return data.secure_url;
    } catch (error) {
      console.error('Erreur upload Cloudinary :', error.message);
      return null;
    }
  };

  const handlePhotoUpload = (e, type) => {
    const files = Array.from(e.target.files);
    if (type === 'checkin') setCheckinPhotos((prev) => [...prev, ...files]);
    else setCheckoutPhotos((prev) => [...prev, ...files]);
  };

  const removePhoto = (idx, type) => {
    if (type === 'checkin') setCheckinPhotos((prev) => prev.filter((_, i) => i !== idx));
    else setCheckoutPhotos((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmitCheckin = async () => {
    setLoading(true);
    try {
      const photosURLs = await Promise.all(checkinPhotos.map(uploadToCloudinary));
      const validPhotos = photosURLs.filter(Boolean);
      const { data } = await axios.post('/checkin/' + reservation.reservationId, {
        reservationId: reservation.reservationId,
        kmStart,
        fuelStart,
        photos: validPhotos,
      });
      if (data) {
        toast.success('Check-in envoyé au propriétaire pour validation');
        onReservationUpdated?.();
      }
    } catch (error) {
      console.error('Erreur check-in :', error.response?.data || error.message);
      toast.error("Erreur d'envoi des données");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitCheckout = async () => {
    setLoading(true);
    try {
      const photosURLs = await Promise.all(checkoutPhotos.map(uploadToCloudinary));
      const validPhotos = photosURLs.filter(Boolean);
      const { data } = await axios.post('/checkout/' + reservation.reservationId, {
        reservationId: reservation.reservationId,
        kmEnd,
        fuelEnd,
        photos: validPhotos,
        comment: commentCheckout,
      });
      if (data) {
        toast.success('Check-out envoyé au propriétaire pour validation');
        onReservationUpdated?.();
      }
    } catch (error) {
      console.error('Erreur check-out :', error.response?.data || error.message);
      toast.error("Erreur dans l'envoi des données");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* ── Statut de la réservation ── */}
      <div className="d-flex align-items-center gap-2 mb-3">
        <i className="bi bi-map text-primary" style={{ fontSize: 18 }}></i>
        <div>
          <p className="mb-0 fw-semibold" style={{ fontSize: 14 }}>
            {moment(reservation.startDate).format('D MMM')}
            <span className="text-muted mx-2">→</span>
            {moment(reservation.endDate).format('D MMM YYYY')}
          </p>
        </div>
      </div>

      {/* ════════════════ CHECK-IN ════════════════ */}
      {reservation.status === 'paid' && (
        <div className="card border-0 bg-body-tertiary rounded-3 p-3 mb-3">
          {/* En-tête */}
          <div className="d-flex align-items-center gap-2 mb-3">
            <div
              className="d-flex align-items-center justify-content-center rounded-circle bg-primary text-white flex-shrink-0"
              style={{ width: 32, height: 32 }}
            >
              <i className="bi bi-geo-alt-fill" style={{ fontSize: 14 }}></i>
            </div>
            <div>
              <p className="mb-0 fw-bold" style={{ fontSize: 14 }}>
                Avant départ — Check-in
              </p>
              <p className="mb-0 text-muted" style={{ fontSize: 11 }}>
                État du véhicule au départ
              </p>
            </div>
          </div>

          {/* Km */}
          <div className="mb-3">
            <label
              htmlFor="kmStart"
              className="form-label fw-semibold mb-1"
              style={{ fontSize: 13 }}
            >
              <i className="bi bi-speedometer2 me-1 text-muted"></i>Kilométrage actuel
            </label>
            <div className="input-group">
              <input
                id="kmStart"
                type="number"
                inputMode="numeric"
                className="form-control"
                placeholder="Ex : 45 230"
                value={kmStart}
                onChange={(e) => setKmStart(e.target.value)}
                min="0"
                style={{ fontSize: 15 }}
              />
              <span className="input-group-text text-muted" style={{ fontSize: 12 }}>
                km
              </span>
            </div>
          </div>

          {/* Carburant — boutons */}
          <div className="mb-3">
            <label className="form-label fw-semibold mb-2" style={{ fontSize: 13 }}>
              <i className="bi bi-fuel-pump me-1 text-muted"></i>Niveau carburant
            </label>
            <div className="d-flex gap-2 flex-wrap">
              {FUEL_OPTIONS.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  className={`btn btn-sm rounded-pill px-3 ${fuelStart === opt ? 'btn-primary' : 'btn-outline-secondary'}`}
                  style={{ fontSize: 13, minWidth: 52 }}
                  onClick={() => setFuelStart(opt)}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          {/* Photos */}
          <div className="mb-3">
            <label className="form-label fw-semibold mb-2" style={{ fontSize: 13 }}>
              <i className="bi bi-camera me-1 text-muted"></i>Photos du véhicule
              <span className="text-muted fw-normal ms-1">
                ({checkinPhotos.length} ajoutée{checkinPhotos.length > 1 ? 's' : ''})
              </span>
            </label>

            {/* Zone d'ajout */}
            <label
              className="d-flex align-items-center justify-content-center gap-2 rounded-3 border border-dashed text-muted"
              style={{
                cursor: 'pointer',
                height: 64,
                borderStyle: 'dashed',
                borderColor: '#ced4da',
                fontSize: 13,
              }}
            >
              <i className="bi bi-plus-circle" style={{ fontSize: 20 }}></i>
              <span>Ajouter des photos</span>
              <input
                type="file"
                multiple
                accept="image/*"
                capture="environment"
                className="d-none"
                onChange={(e) => handlePhotoUpload(e, 'checkin')}
              />
            </label>

            {/* Prévisualisation */}
            {checkinPhotos.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mt-2">
                {checkinPhotos.map((file, idx) => (
                  <div key={idx} className="position-relative" style={{ width: 72, height: 72 }}>
                    <img
                      src={URL.createObjectURL(file)}
                      alt="preview"
                      className="rounded-2 object-fit-cover w-100 h-100"
                      style={{ objectFit: 'cover' }}
                    />
                    <button
                      type="button"
                      className="btn btn-danger btn-sm position-absolute top-0 end-0 rounded-circle p-0 d-flex align-items-center justify-content-center"
                      style={{
                        width: 20,
                        height: 20,
                        fontSize: 10,
                        transform: 'translate(40%,-40%)',
                      }}
                      onClick={() => removePhoto(idx, 'checkin')}
                    >
                      <i className="bi bi-x"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Bouton valider */}
          <button
            className="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2"
            onClick={handleSubmitCheckin}
            disabled={loading || !kmStart || !fuelStart || checkinPhotos.length === 0}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm" />
                Envoi en cours…
              </>
            ) : (
              <>
                <i className="bi bi-check2-circle"></i>Valider le Check-in
              </>
            )}
          </button>
          {(!kmStart || !fuelStart || checkinPhotos.length === 0) && (
            <p className="text-muted text-center mt-2 mb-0" style={{ fontSize: 11 }}>
              Renseignez le kilométrage, le carburant et au moins une photo
            </p>
          )}
        </div>
      )}

      {/* ── Check-in en cours de validation ── */}
      {reservation.status === 'checkin_pending_validation' && (
        <div className="alert alert-info d-flex align-items-center gap-2 rounded-3">
          <i className="bi bi-hourglass-split flex-shrink-0"></i>
          <div>
            <strong>Check-in en attente</strong>
            <p className="mb-0 small">Le propriétaire examine votre état des lieux.</p>
          </div>
        </div>
      )}

      {/* ════════════════ CHECK-OUT ════════════════ */}
      {reservation.status === 'in_progress' && (
        <>
          <div className="alert alert-success d-flex align-items-center gap-2 rounded-3 mb-3">
            <i className="bi bi-check-circle-fill flex-shrink-0"></i>
            <span>Check-in validé — location en cours</span>
          </div>

          <div className="card border-0 bg-body-tertiary rounded-3 p-3">
            {/* En-tête */}
            <div className="d-flex align-items-center gap-2 mb-3">
              <div
                className="d-flex align-items-center justify-content-center rounded-circle bg-success text-white flex-shrink-0"
                style={{ width: 32, height: 32 }}
              >
                <i className="bi bi-flag-fill" style={{ fontSize: 14 }}></i>
              </div>
              <div>
                <p className="mb-0 fw-bold" style={{ fontSize: 14 }}>
                  Retour — Check-out
                </p>
                <p className="mb-0 text-muted" style={{ fontSize: 11 }}>
                  État du véhicule au retour
                </p>
              </div>
            </div>

            {/* Km */}
            <div className="mb-3">
              <label
                htmlFor="kmEnd"
                className="form-label fw-semibold mb-1"
                style={{ fontSize: 13 }}
              >
                <i className="bi bi-speedometer2 me-1 text-muted"></i>Kilométrage final
              </label>
              <div className="input-group">
                <input
                  id="kmEnd"
                  type="number"
                  inputMode="numeric"
                  className="form-control"
                  placeholder="Ex : 45 890"
                  value={kmEnd}
                  onChange={(e) => setKmEnd(e.target.value)}
                  min="0"
                  style={{ fontSize: 15 }}
                />
                <span className="input-group-text text-muted" style={{ fontSize: 12 }}>
                  km
                </span>
              </div>
            </div>

            {/* Carburant — boutons */}
            <div className="mb-3">
              <label className="form-label fw-semibold mb-2" style={{ fontSize: 13 }}>
                <i className="bi bi-fuel-pump me-1 text-muted"></i>Niveau carburant
              </label>
              <div className="d-flex gap-2 flex-wrap">
                {FUEL_OPTIONS.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    className={`btn btn-sm rounded-pill px-3 ${fuelEnd === opt ? 'btn-success' : 'btn-outline-secondary'}`}
                    style={{ fontSize: 13, minWidth: 52 }}
                    onClick={() => setFuelEnd(opt)}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>

            {/* Photos */}
            <div className="mb-3">
              <label className="form-label fw-semibold mb-2" style={{ fontSize: 13 }}>
                <i className="bi bi-camera me-1 text-muted"></i>Photos du véhicule
                <span className="text-muted fw-normal ms-1">
                  ({checkoutPhotos.length} ajoutée{checkoutPhotos.length > 1 ? 's' : ''})
                </span>
              </label>

              <label
                className="d-flex align-items-center justify-content-center gap-2 rounded-3 border text-muted"
                style={{
                  cursor: 'pointer',
                  height: 64,
                  borderStyle: 'dashed',
                  borderColor: '#ced4da',
                  fontSize: 13,
                }}
              >
                <i className="bi bi-plus-circle" style={{ fontSize: 20 }}></i>
                <span>Ajouter des photos</span>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  capture="environment"
                  className="d-none"
                  onChange={(e) => handlePhotoUpload(e, 'checkout')}
                />
              </label>

              {checkoutPhotos.length > 0 && (
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {checkoutPhotos.map((file, idx) => (
                    <div key={idx} className="position-relative" style={{ width: 72, height: 72 }}>
                      <img
                        src={URL.createObjectURL(file)}
                        alt="preview"
                        className="rounded-2 w-100 h-100"
                        style={{ objectFit: 'cover' }}
                      />
                      <button
                        type="button"
                        className="btn btn-danger btn-sm position-absolute top-0 end-0 rounded-circle p-0 d-flex align-items-center justify-content-center"
                        style={{
                          width: 20,
                          height: 20,
                          fontSize: 10,
                          transform: 'translate(40%,-40%)',
                        }}
                        onClick={() => removePhoto(idx, 'checkout')}
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Commentaire */}
            <div className="mb-3">
              <label
                htmlFor="commentCheckout"
                className="form-label fw-semibold mb-1"
                style={{ fontSize: 13 }}
              >
                <i className="bi bi-chat-text me-1 text-muted"></i>Commentaire{' '}
                <span className="text-muted fw-normal">(optionnel)</span>
              </label>
              <textarea
                id="commentCheckout"
                className="form-control"
                rows={3}
                placeholder="Dommages constatés, plein refait, remarques…"
                value={commentCheckout}
                onChange={(e) => setCommentCheckout(e.target.value)}
                style={{ fontSize: 14, resize: 'none' }}
              />
            </div>

            <button
              className="btn btn-success w-100 d-flex align-items-center justify-content-center gap-2"
              onClick={handleSubmitCheckout}
              disabled={loading || !kmEnd || !fuelEnd || checkoutPhotos.length === 0}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm" />
                  Envoi en cours…
                </>
              ) : (
                <>
                  <i className="bi bi-flag-fill"></i>Valider le Check-out
                </>
              )}
            </button>
            {(!kmEnd || !fuelEnd || checkoutPhotos.length === 0) && (
              <p className="text-muted text-center mt-2 mb-0" style={{ fontSize: 11 }}>
                Renseignez le kilométrage, le carburant et au moins une photo
              </p>
            )}
          </div>
        </>
      )}

      {/* ── États finaux ── */}
      {reservation.status === 'completed' && (
        <div className="alert alert-success d-flex align-items-center gap-2 rounded-3">
          <i className="bi bi-patch-check-fill flex-shrink-0" style={{ fontSize: 20 }}></i>
          <div>
            <strong>Location terminée avec succès</strong>
            <p className="mb-0 small">Merci d'avoir utilisé Moovicar !</p>
          </div>
        </div>
      )}

      {reservation.status === 'accepted' && (
        <div className="alert alert-info d-flex align-items-center gap-2 rounded-3">
          <i className="bi bi-info-circle-fill flex-shrink-0"></i>
          <span>Réservation acceptée — veuillez compléter le paiement pour confirmer.</span>
        </div>
      )}

      {reservation.status === 'checkin_refused' && (
        <div className="alert alert-warning d-flex align-items-center gap-2 rounded-3">
          <i className="bi bi-exclamation-triangle-fill flex-shrink-0"></i>
          <div>
            <strong>Check-in refusé</strong>
            <p className="mb-0 small">
              Le propriétaire a refusé l'état des lieux. Contactez-le via la messagerie.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReservationFlow;
